import type {
  Dispatch,
  ReactNode,
  SetStateAction,
  SubmitEventHandler,
} from 'react';
import type {
  ContentObjectFormContextProps,
  VariantCreateFn,
  VariantDeleteFn,
} from '../../contexts/ContentObjectFormContext';
import type { CtoCustomFieldProps } from '../../form-hooks/form-components/CtoCustomField/CtoCustomField';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type { FormSubmitMetadata } from '../../hooks/useAutoSave';
import type {
  ContentObject,
  ContentTypeDefinition,
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
  SlugRule,
  WithOptionalInternalProps,
  WithoutInternalProps,
} from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';

import {
  setFormSubmitErrors,
  showValidationErrorToast,
} from '../../form-hooks/lib/helpers';
import { getEnv } from '../../lib/env';
import FlotiqPlugins from '../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { FormAfterSubmitEvent } from '../../lib/flotiq-plugins/plugin-events/FormAfterSubmitEvent';
import { EMPTY_OBJECT } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import useMediaUpdateVariants from '../../hooks/api/useMediaUpdateVariants';
import { useAutoSave } from '../../hooks/useAutoSave';

import ContentObjectFormContext from '../../contexts/ContentObjectFormContext';
import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';
import WebsocketContext from '../../contexts/WebsocketContext';

import useYjsFormObserver from '../../form-hooks/collaboration/useYjsFormObserver';
import { useAppForm } from '../../form-hooks/form';
import VariantModalForm from '../VariantModalForm/VariantModalForm';
import { generateZodSchemaForCto } from './helpers/generateZodSchemaForCto';
import { getInitialData } from './helpers/getInitialData';

const defaultFormSubmitMetadata: FormSubmitMetadata = {
  disableLoader: false,
};

/**
 * Function that will be called on form validate. If there will be no result,
 * there will be zod validation based on schema. If returns errors, they will be passed
 * to form. Errors should be an object with field names as flat keys and error messages as values.
 *
 * @returns Object or null if there are no errors
 */
export type ContentObjectCustomValidateFn = (
  values: WithoutInternalProps<ContentObject>,
) => Promise<Record<string, string | string[]> | null>;

/**
 * Function that will be called on form submit. Should return array with new object data
 * and errors.
 */
export type ContentObjectSubmitFn = (
  values: WithoutInternalProps<ContentObject>,
  disableLoader?: boolean,
) => Promise<
  [
    /** New object data */
    WithOptionalInternalProps<ContentObject> | null,
    /** Errors if occured during submit */
    Record<string, string | string[]> | null,
    /** Has errors boolean */
    boolean,
  ]
>;

export type ContentObjectGetCustomPropsFn = (
  key: string,
  props: ContentTypePropertyConfig,
  schema: ContentTypePropertySchema,
  isRequired: boolean,
) => Partial<CtoCustomFieldProps>;

export type SlugRulesType = {
  targets: Record<string, SlugRule>;
  sources: Record<string, SlugRule>;
};

interface ContentObjectFormProps {
  /**
   * Content type
   */
  contentType:
    | (WithOptionalInternalProps<ContentTypeDefinition> & {
        nonCtdSchema?: boolean;
      })
    | null;
  /**
   * On submit callback
   */
  onSubmit?: ContentObjectSubmitFn;
  /**
   * Content object to edit
   */
  contentObject?: WithOptionalInternalProps<ContentObject> | null;
  /**
   * If form is used for object editing
   */
  isEditing?: boolean;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * Content object form id
   */
  formId?: string;
  /**
   * On validate callback
   */
  onValidate?: ContentObjectCustomValidateFn;
  /**
   * Form enable reinitialize
   */
  enableReinitialize?: boolean;
  /**
   * If build in validation is off
   */
  disabledBuildInValidation?: boolean;
  /**
   * Additional form classes
   */
  additionalFormClasses?: string;
  /**
   * Test id for page
   */
  testId?: string;
  /**
   * Should the form have switches for enabling fields
   */
  isPatchable?: boolean;
  /**
   * List of fields to patch
   */
  overriddenFields?: ContentObjectFormContextProps['overriddenFields'];
  /**
   * Function to set overridden fields
   */
  setOverriddenFields?: ContentObjectFormContextProps['setOverriddenFields'];
  /**
   * Count of edited objects
   */
  editedCount?: number;
  /**
   * Unique key for form. Used in plugins to distinguish different forms and in editors (e.g. block editor).
   */
  formUniqueKey?: string;
  /**
   * Callback for updating form state
   */
  setFormState?: SetFormApiFn;
  /**
   * Callback for updating the count of added fields
   */
  setAddedFieldsCount?: Dispatch<SetStateAction<number>>;
  /**
   * Customize render of the fields
   */
  getCustomFieldProps?: ContentObjectGetCustomPropsFn;
  /**
   * Status bar rendered above the form fields on mobile only
   */
  statusBar?: ReactNode;
}

const ContentObjectForm = ({
  contentObject,
  contentType,
  isEditing = false,
  onSubmit,
  disabled = false,
  formId = 'cto-form',
  onValidate,
  disabledBuildInValidation = false,
  additionalFormClasses = '',
  testId = '',
  isPatchable = false,
  overriddenFields = EMPTY_OBJECT,
  setOverriddenFields,
  editedCount = 1,
  formUniqueKey,
  getCustomFieldProps,
  setAddedFieldsCount,
  setFormState,
  statusBar,
}: ContentObjectFormProps) => {
  const { t } = useTranslation();
  const { contentTypeName } = useParams();
  const modal = useModals();
  const { updateVariants } = useMediaUpdateVariants();
  const { permissions } = use(UserContext);
  const { yjsDocument, isYjsDirty } = use(WebsocketContext);

  const isAutoSaveEnabled = getEnv('VITE_AUTO_SAVE_ENABLE') === 'true';

  const schema = useMemo(() => {
    if (!contentType?.schemaDefinition?.allOf[1]?.properties) return {};
    return Object.entries(
      contentType.schemaDefinition.allOf[1].properties,
    ).reduce(
      (acc, [key, value]) => {
        if (
          !permissions.canCo(
            contentType?.name || contentTypeName,
            RolePermissions.PERMISSIONS_TYPES.READ,
            key,
          )
        )
          return acc;
        acc[key] = value;
        return acc;
      },
      {} as Record<string, ContentTypePropertySchema>,
    );
  }, [contentType, contentTypeName, permissions]);

  const properties = useMemo(() => {
    if (!contentType?.metaDefinition?.propertiesConfig) return {};
    if (!contentType.metaDefinition?.order)
      return contentType.metaDefinition?.propertiesConfig;
    return contentType.metaDefinition.order.reduce(
      (acc, key) => {
        if (
          !permissions.canCo(
            contentType?.name || contentTypeName,
            RolePermissions.PERMISSIONS_TYPES.READ,
            key,
          )
        )
          return acc;
        acc[key] = contentType.metaDefinition?.propertiesConfig[key];
        return acc;
      },
      {} as Record<string, ContentTypePropertyConfig>,
    );
  }, [contentType, permissions, contentTypeName]);

  const ctdRequired = contentType?.schemaDefinition?.required;

  const requiredFields = useMemo(() => {
    if (!ctdRequired || isPatchable) return {};
    return ctdRequired.reduce(
      (acc, key) => {
        acc[key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [ctdRequired, isPatchable]);

  const sluggedFields = useMemo(() => {
    const slugs = contentType?.slugs || [];
    return slugs.reduce(
      (accumulator: SlugRulesType, currentValue) => {
        if (currentValue.active) {
          accumulator.targets[currentValue.target] = currentValue;
          accumulator.sources[currentValue.source] = currentValue;
        }
        return accumulator;
      },
      { sources: {}, targets: {} },
    );
  }, [contentType]);

  const validationObject = useMemo(
    () =>
      generateZodSchemaForCto(
        schema,
        t,
        requiredFields,
        properties,
        undefined,
        isPatchable,
      ),
    [schema, t, requiredFields, properties, isPatchable],
  );

  const initialValues = useMemo(
    () =>
      getInitialData(
        properties,
        schema,
        contentObject,
        isEditing && !!contentObject,
        yjsDocument,
      ),
    [properties, schema, contentObject, yjsDocument, isEditing],
  );

  const onVariantCreate = useCallback<VariantCreateFn>(
    async (media) => {
      if (!media) return;
      const newVariant = await modal({
        title: t('MediaEdit.AddVariant'),
        content: (
          <VariantModalForm
            media={media}
            variantsNames={(media.variants || []).map(
              (variant) => variant.name,
            )}
          />
        ),
        size: 'xl',
        dialogAdditionalClasses: 'max-h-screen',
      });

      const newVariants = [...(media.variants || []), newVariant];

      if (newVariant) {
        return await updateVariants(media.id, newVariants);
      }

      return [null, false];
    },
    [modal, t, updateVariants],
  );

  const onVariantDelete = useCallback<VariantDeleteFn>(
    async (media, idx) => {
      if (!media?.variants?.length) return [];

      const newVariants = media.variants.filter(
        (_, variantIdx) => idx !== variantIdx,
      );

      return await updateVariants(media.id, newVariants, true);
    },
    [updateVariants],
  );

  const contentObjectFormContextValue = useMemo(
    () => ({
      contentType,
      isEditing,
      initialData: contentObject,
      onVariantCreate,
      onVariantDelete,
      isPatchable,
      overriddenFields,
      setOverriddenFields,
      editedCount,
      formUniqueKey,
    }),
    [
      contentObject,
      contentType,
      isEditing,
      onVariantCreate,
      onVariantDelete,
      isPatchable,
      overriddenFields,
      setOverriddenFields,
      editedCount,
      formUniqueKey,
    ],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmitMeta: defaultFormSubmitMetadata,
    onSubmit: async ({ value, formApi, meta }) => {
      if (!onSubmit) return;
      const newValues = { ...value };
      for (const property in newValues) {
        if (
          !permissions.canCo(
            contentType?.name || contentTypeName,
            isEditing
              ? RolePermissions.PERMISSIONS_TYPES.UPDATE
              : RolePermissions.PERMISSIONS_TYPES.CREATE,
            property,
          )
        ) {
          delete newValues[property];
        }
      }
      const [newObject, errors, hasErrors] = await onSubmit(
        newValues,
        meta.disableLoader,
      );

      const success = !hasErrors && (!errors || !Object.keys(errors).length);

      if (success) {
        /**
         * The saved object may differ from submitted values, while refreshed
         * initialValues arrive on a later render. Keeping current defaultValues
         * here is needed so TanStack Form does not restore stale hook
         * defaults before the refreshed initialValues are applied.
         * After resseting the form and updating initialValues, defaultValues are "fresh".
         *
         * https://github.com/TanStack/form/issues/1798#issuecomment-3390641121
         */
        form.reset(
          getInitialData(properties, schema, newObject, true, yjsDocument),
          { keepDefaultValues: true },
        );
      } else {
        const filteredErrors = setFormSubmitErrors(formApi, errors);
        showValidationErrorToast(filteredErrors);
      }

      FlotiqPlugins.run(
        'flotiq.form::after-submit',
        new FormAfterSubmitEvent({
          success,
          contentObject: newObject,
          errors: !success ? errors : undefined,
        }),
      );
    },
    validators: {
      ...(disabledBuildInValidation ? {} : { onChange: validationObject }),
      ...(typeof onValidate === 'function'
        ? {
            onChangeAsync: async ({
              value,
            }: {
              value: WithoutInternalProps<ContentObject>;
            }) => {
              const errors = await onValidate(value);
              if (Object.keys(errors || {}).length > 0) {
                return { fields: errors };
              }
            },
          }
        : {}),
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  useAutoSave(
    isAutoSaveEnabled && (contentType?.autoSave || false),
    form.state.isDirty,
    form.handleSubmit,
  );

  useYjsFormObserver(form, yjsDocument);

  const renderFormField = (
    key: string,
    props: ContentTypePropertyConfig,
    schema: ContentTypePropertySchema,
    isRequired: boolean,
  ) => {
    const customProps = getCustomFieldProps?.(key, props, schema, isRequired);
    return (
      <form.CtoCustomField
        key={key}
        name={key}
        properties={props}
        schema={schema}
        isRequired={isRequired}
        sluggedFields={sluggedFields}
        disabled={
          disabled ||
          !permissions.canCo(
            contentType?.name || contentTypeName,
            isEditing
              ? RolePermissions.PERMISSIONS_TYPES.UPDATE
              : RolePermissions.PERMISSIONS_TYPES.CREATE,
            key,
          )
        }
        additionalClasses="max-w-3xl"
        testId={testId}
        {...customProps}
      />
    );
  };

  return (
    <ContentObjectFormContext value={contentObjectFormContextValue}>
      <form.AppForm>
        <form
          className={twMerge(
            'flex flex-col space-y-3 md:space-y-6 pb-0 md:pb-5',
            additionalFormClasses,
          )}
          id={formId}
          onSubmit={handleSubmit}
          noValidate
          {...getTestProps(testId)}
        >
          <form.CustomFormElement setCount={setAddedFieldsCount} />
          {statusBar}
          {Object.keys(properties).map((key) =>
            renderFormField(
              key,
              properties[key],
              schema[key],
              requiredFields[key],
            ),
          )}

          <form.DirtyHandler dirty={isYjsDirty} />
          <form.ValidationToastHandler />
          <form.FormStateHandler setFormState={setFormState} />
          <form.FocusAfterSaveHandler formId={formId} />
        </form>
      </form.AppForm>
    </ContentObjectFormContext>
  );
};

export default ContentObjectForm;
